<script setup>
/**
 * The questions your customers asked that your assistant had no answer for.
 *
 * This is the most actionable region on the Activity screen and the one the plan singles
 * out, so it keeps the row grammar the whole section uses — **[mark] [what] [count]
 * [verb]** — and it keeps the same verb the Overview's queue uses for the same fact,
 * *Teach an answer*, pointing at the same destination. Two screens surfacing one problem
 * must not name it two different ways or send the owner to two different places.
 *
 * IT IS NOT THE OVERVIEW'S QUEUE, though, and the difference is deliberate. Overview shows
 * the top five, mixed in with disconnected channels and failed sources, because it is a
 * to-do list. This is the full list for a period the owner chose, alone, ranked by how
 * often each question was asked — because here she is not triaging, she is deciding what
 * to write next. The count is therefore the ranking key and sits on every row, not just on
 * the repeated ones.
 *
 * THE EMPTY STATE IS THE GOOD STATE. "It answered everything it was asked" is the sentence
 * an owner most wants from this page, so it gets a solid card and the accent check mark —
 * the same shape as `AttentionQueue`'s all-clear, on purpose — rather than the grey "no
 * results" that would make a healthy account look like a broken query.
 *
 * `query` is a CUSTOMER'S OWN WORDS: untrusted text, and in this market as likely to be
 * Arabic as English. It goes in a `<bdi>` inside a `dir="auto"` paragraph, and the count
 * and the verb live in a trailing cluster that follows the PAGE's direction — so the
 * action is in the same place on an Arabic row as on an English one, which is the fix a
 * blind critic asked for on the Overview's list.
 */
import { nextTick, ref } from 'vue';

import TeachAnswerDialog from './TeachAnswerDialog.vue';
import { useWasloI18n } from '../i18n.js';
import { toIntlLocale } from '../helpers/intl.js';

defineProps({
  /**
   * `[{ query, count, id, ways }]`, most asked first: the order the BFF returns. `id` is the
   * group of wordings (P-6.6), `null` from an older BFF; `ways` is how many wordings.
   */
  items: { type: Array, default: () => [] },
  /** Chatwoot account id. Null (mounted outside a matched route) → no links. */
  accountId: { type: Number, default: null },
});

/** A saved fix: the page reads its questions again, and the fixed one is gone. */
const emit = defineEmits(['fixed']);

const { t, locale } = useWasloI18n();

const formatCount = value =>
  new Intl.NumberFormat(toIntlLocale(locale.value)).format(value);

// Ease C3 (P-6.4): "Fix it" opens the fix dialog right here, with the customer's words in the
// question box and the group it answers, instead of sending her to another page to find it.
const fixing = ref(null);
const fixRef = ref(null);
const fix = async item => {
  fixing.value = item;
  await nextTick();
  fixRef.value?.open();
};
</script>

<template>
  <ul
    v-if="items.length"
    class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
    data-waslo-unanswered-list
  >
    <li
      v-for="item in items"
      :key="item.query"
      class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center"
      data-waslo-unanswered-row
    >
      <span
        class="grid rounded-lg shrink-0 place-items-center size-9 bg-n-amber-3"
        aria-hidden="true"
      >
        <span class="size-4 i-lucide-message-circle-question text-n-amber-11" />
      </span>

      <p
        class="mb-0 text-sm font-medium break-words min-w-0 text-n-slate-12"
        dir="auto"
        data-waslo-unanswered-query
      >
        <bdi>{{ item.query }}</bdi>
      </p>

      <div class="flex items-center gap-3 shrink-0 sm:ms-auto ms-0">
        <span
          class="text-xs font-medium text-n-slate-11"
          data-waslo-unanswered-count
        >
          {{ t('WASLO.ACTIVITY.QUESTIONS.ASKED', { count: item.count }) }}
        </span>
        <span
          v-if="item.ways > 1"
          class="text-xs text-n-slate-11"
          data-waslo-unanswered-ways
        >
          {{
            t('WASLO.GAPS.WAYS', { count: formatCount(item.ways) }, item.ways)
          }}
        </span>
        <button
          v-if="accountId !== null"
          type="button"
          class="inline-flex items-center h-8 gap-1 px-3 text-sm font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-slate-4"
          data-waslo-unanswered-verb
          @click="fix(item)"
        >
          {{ t('WASLO.ACTIVITY.QUESTIONS.VERB') }}
        </button>
        <span
          v-else
          class="inline-flex items-center h-8 px-3 text-sm font-medium text-n-slate-11"
          data-waslo-unanswered-verb
        >
          {{ t('WASLO.ACTIVITY.QUESTIONS.VERB') }}
        </span>
      </div>
    </li>
  </ul>

  <div
    v-else
    class="flex items-start w-full gap-4 px-6 py-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
    data-waslo-unanswered-clear
  >
    <span
      class="grid shrink-0 place-items-center size-9 rounded-xl bg-n-blue-3"
      aria-hidden="true"
    >
      <span class="size-5 i-lucide-check text-n-blue-10" />
    </span>
    <div class="flex flex-col gap-1">
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('WASLO.ACTIVITY.QUESTIONS.CLEAR_TITLE') }}
      </p>
      <p class="mb-0 text-sm leading-6 text-n-slate-11">
        {{ t('WASLO.ACTIVITY.QUESTIONS.CLEAR_BODY') }}
      </p>
    </div>
  </div>

  <TeachAnswerDialog
    v-if="fixing"
    :key="fixing.query"
    ref="fixRef"
    mode="fix"
    :account-id="accountId"
    :gap-id="fixing.id"
    :suggested-question="fixing.query"
    @saved="emit('fixed')"
  />
</template>
